/* eslint-disable no-param-reassign */
import { createAction } from '@reduxjs/toolkit';

import {
  findGalleryOrderEntry,
  PRODUCT_VIEW_LAYER,
} from '../../../components/AssetTargetsDialog/helpers/assetProductLinks';
import { appendProductLink, appendVariantLink } from '../../helpers/assets/applyAssetProductLinks';
import {
  setListingAssets,
  setListingVariantAssets,
  setMasterAssets,
  setMasterVariantAssets,
} from './entityAdapter';

export const appendMasterGalleryAssets = createAction('products/appendMasterGalleryAssets');
export const appendListingGalleryAssets = createAction('products/appendListingGalleryAssets');
export const appendVariantGalleryAssets = createAction('products/appendVariantGalleryAssets');

const collectPoolAssets = (pools = {}) => Object.values(pools).flat().filter(Boolean);

const findAssetInPools = (pools, assetId) => {
  const id = String(assetId);
  return collectPoolAssets(pools).find((asset) => String(asset._id) === id);
};

/**
 * Resolve asset objects for optimistic master gallery insert.
 * Lookup: masterAssets → listingAssets → variant pools → catalog → media library modal → clipboard → stub.
 */
export const resolveAssetsFromState = (state, assetIds = []) => {
  const productAssets = state?.products?.entities?.assets || {};
  const catalogItems = state?.assets?.items || [];
  const modalItems = state?.mediaLibraryModal?.items || [];

  return assetIds.map((assetId) => {
    const id = String(assetId);
    const fromMaster = findAssetInPools(productAssets.masterAssets, id);
    if (fromMaster) return fromMaster;

    const fromListing = findAssetInPools(productAssets.listingAssets, id);
    if (fromListing) return fromListing;

    const fromMasterVariant = findAssetInPools(productAssets.masterVariantAssets, id);
    if (fromMasterVariant) return fromMasterVariant;

    const fromListingVariant = findAssetInPools(productAssets.listingVariantAssets, id);
    if (fromListingVariant) return fromListingVariant;

    const fromCatalog = catalogItems.find((asset) => String(asset._id) === id);
    if (fromCatalog) return fromCatalog;

    const fromModal = modalItems.find((asset) => String(asset._id) === id);
    if (fromModal) return fromModal;

    const clipboardMap = state?.mediaLibraryModal?.selectedAssetsById || {};
    const fromClipboard = clipboardMap[id] || clipboardMap[assetId];
    if (fromClipboard) return fromClipboard;

    return { _id: assetId, products: [], customOrder: [] };
  });
};

export const getNextGalleryCustomOrderIndex = (
  assets = [],
  productId,
  layer = PRODUCT_VIEW_LAYER.MASTER,
) => (
  assets.reduce((max, asset) => {
    const index = findGalleryOrderEntry(asset, productId, layer)?.index;
    if (!Number.isFinite(index)) return max;
    return index >= max ? index + 1 : max;
  }, 0)
);

export const getNextMasterCustomOrderIndex = (assets = [], productId) => (
  getNextGalleryCustomOrderIndex(assets, productId, PRODUCT_VIEW_LAYER.MASTER)
);

const upsertGalleryCustomOrder = (customOrder = [], productId, index, layer) => {
  const isDraft = layer === PRODUCT_VIEW_LAYER.STAGING;
  const next = [...customOrder];
  const entryIndex = next.findIndex((entry) => (
    String(entry?.productId) === String(productId)
    && (isDraft ? entry?.isDraft === true : entry?.isDraft !== true)
  ));
  const nextEntry = isDraft
    ? { productId, isDraft: true, index }
    : { productId, index };

  if (entryIndex >= 0) {
    next[entryIndex] = { ...next[entryIndex], ...nextEntry };
  } else {
    next.push(nextEntry);
  }

  return next;
};

const upsertMasterCustomOrder = (customOrder = [], productId, index) => (
  upsertGalleryCustomOrder(customOrder, productId, index, PRODUCT_VIEW_LAYER.MASTER)
);

export const appendMissingGalleryAssets = (fetchedAssets = [], mergeAssets = []) => {
  const fetched = Array.isArray(fetchedAssets) ? fetchedAssets : [];
  if (!mergeAssets?.length) return fetched;

  const fetchedIds = new Set(fetched.map((asset) => String(asset._id)));
  const missing = mergeAssets.filter((asset) => asset?._id && !fetchedIds.has(String(asset._id)));
  if (!missing.length) return fetched;

  return [...fetched, ...missing];
};

/**
 * Build assets to append at the end of the master gallery (skip duplicates).
 */
export const buildOptimisticMasterAssets = ({
  assets = [],
  product,
  existingMasterAssets = [],
} = {}) => {
  if (!product?._id || !assets.length) return [];

  const existingIds = new Set(existingMasterAssets.map((asset) => String(asset._id)));
  let nextIndex = getNextMasterCustomOrderIndex(existingMasterAssets, product._id);
  const masterLink = {
    _id: product._id,
    title: product.title || product._id,
    type: 'master',
  };

  return assets.reduce((acc, asset) => {
    if (!asset?._id || existingIds.has(String(asset._id))) return acc;

    const optimisticAsset = {
      ...asset,
      products: appendProductLink(asset.products, masterLink),
      customOrder: upsertMasterCustomOrder(asset.customOrder, product._id, nextIndex),
    };
    nextIndex += 1;
    existingIds.add(String(asset._id));
    acc.push(optimisticAsset);
    return acc;
  }, []);
};

/**
 * Keep fetched gallery order; re-append mergeAssets missing from search at the end.
 */
export const mergeMasterGalleryAssets = (fetchedAssets = [], mergeAssets = [], productId) => {
  const fetched = Array.isArray(fetchedAssets) ? fetchedAssets : [];
  if (!mergeAssets?.length || !productId) return fetched;

  const fetchedIds = new Set(fetched.map((asset) => String(asset._id)));
  const missing = mergeAssets.filter((asset) => asset?._id && !fetchedIds.has(String(asset._id)));
  if (!missing.length) return fetched;

  let nextIndex = getNextMasterCustomOrderIndex(fetched, productId);
  const appended = missing.map((asset) => {
    const nextAsset = {
      ...asset,
      customOrder: upsertMasterCustomOrder(asset.customOrder, productId, nextIndex),
    };
    nextIndex += 1;
    return nextAsset;
  });

  return [...fetched, ...appended];
};

export const applyAppendMasterGalleryAssetsToState = (state, { productId, assets }) => {
  if (!productId || !assets?.length) return;

  const existing = state.entities?.assets?.masterAssets?.[productId]
    || state.items.find((product) => product._id === productId)?.assets
    || [];
  const existingIds = new Set(existing.map((asset) => String(asset._id)));
  const toAppend = assets.filter((asset) => asset?._id && !existingIds.has(String(asset._id)));
  if (!toAppend.length) return;

  const nextAssets = [...existing, ...toAppend];
  state.items.forEach((product) => {
    if (product._id === productId) {
      product.assets = nextAssets;
    }
  });
  setMasterAssets(state, productId, nextAssets);
};

export const buildOptimisticListingGalleryAssets = ({
  assets = [],
  listingProduct,
  existingListingAssets = [],
} = {}) => {
  if (!listingProduct?._id || !assets.length) return [];

  const existingIds = new Set(existingListingAssets.map((asset) => String(asset._id)));
  let nextIndex = getNextGalleryCustomOrderIndex(
    existingListingAssets,
    listingProduct._id,
    PRODUCT_VIEW_LAYER.STAGING,
  );

  return assets.reduce((acc, asset) => {
    if (!asset?._id || existingIds.has(String(asset._id))) return acc;

    acc.push({
      ...asset,
      products: appendProductLink(asset.products, listingProduct),
      customOrder: upsertGalleryCustomOrder(
        asset.customOrder,
        listingProduct._id,
        nextIndex,
        PRODUCT_VIEW_LAYER.STAGING,
      ),
    });
    nextIndex += 1;
    existingIds.add(String(asset._id));
    return acc;
  }, []);
};

export const buildOptimisticVariantAssets = ({
  assets = [],
  variantLink,
  existingVariantAssets = [],
} = {}) => {
  if (!variantLink?.variantId || !assets.length) return [];

  const existingIds = new Set(existingVariantAssets.map((asset) => String(asset._id)));
  return assets.reduce((acc, asset) => {
    if (!asset?._id || existingIds.has(String(asset._id))) return acc;

    acc.push({
      ...asset,
      productVariants: appendVariantLink(asset.productVariants, variantLink),
    });
    existingIds.add(String(asset._id));
    return acc;
  }, []);
};

export const applyAppendListingGalleryAssetsToState = (state, { listingProductId, assets }) => {
  if (!listingProductId || !assets?.length) return;

  const existing = state.entities?.assets?.listingAssets?.[listingProductId] || [];
  const nextAssets = appendMissingGalleryAssets(existing, assets);
  if (nextAssets === existing) return;

  setListingAssets(state, listingProductId, nextAssets);
};

export const applyAppendVariantGalleryAssetsToState = (state, {
  productId,
  variantId,
  channelId,
  assets,
}) => {
  if (!variantId || !assets?.length) return;

  if (channelId) {
    const existing = state.entities?.assets?.listingVariantAssets?.[variantId] || [];
    const nextAssets = appendMissingGalleryAssets(existing, assets);
    if (nextAssets === existing) return;

    setListingVariantAssets(state, variantId, nextAssets);
    return;
  }

  const existing = state.entities?.assets?.masterVariantAssets?.[variantId]
    || state.items.find((product) => product._id === productId)?.variantAssetsById?.[variantId]
    || [];
  const nextAssets = appendMissingGalleryAssets(existing, assets);
  if (nextAssets === existing) return;

  state.items.forEach((product) => {
    if (product._id !== productId) return;
    if (!product.variantAssetsById) product.variantAssetsById = {};
    product.variantAssetsById[variantId] = nextAssets;
  });
  setMasterVariantAssets(state, variantId, nextAssets);
};
